'use client';

import { PropsWithChildren } from 'react';

import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import {
  coinbaseWallet,
  ledgerWallet,
  metaMaskWallet,
  okxWallet,
  phantomWallet,
  rainbowWallet,
  safeWallet,
  walletConnectWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { Config } from '@wagmi/core';
import { createConfig, WagmiProvider as BaseWagmiProvider } from 'wagmi';

import { env } from '@web/utils/env';
import { chains, transports } from '@web/utils/rainbowkit/chains';

// When WalletConnect's projectId is a placeholder (e.g. local dev without a
// real cloud.walletconnect.com project), the WC SDK fails to fetch its
// recommended wallets list and crashes the entire app at boot. Detect that
// and fall back to injected-only wallets (MetaMask, Phantom, OKX) which
// don't need a real project ID.
const HAS_REAL_WC_PROJECT =
  !!env.WALLETCONNECT_PROJECT_ID && env.WALLETCONNECT_PROJECT_ID.length > 4;

const walletsConfig = HAS_REAL_WC_PROJECT
  ? [
      {
        groupName: 'Recommended',
        wallets: [coinbaseWallet],
      },
      {
        groupName: 'Others',
        wallets: [
          walletConnectWallet,
          okxWallet,
          metaMaskWallet,
          rainbowWallet,
          phantomWallet,
          ledgerWallet,
          safeWallet,
        ],
      },
    ]
  : [
      {
        groupName: 'Recommended',
        wallets: [metaMaskWallet, phantomWallet, okxWallet],
      },
    ];

/**
 * The public build keeps wagmi only for its on-chain read hooks and registers
 * no connectors: nothing here can sign or transact, and every connector
 * announces itself and probes the page for an injected provider. A site
 * showing an NFT collection while asking for a wallet provider is also what
 * wallet extensions score as a drainer, which is worth not doing.
 */
const shellConfig = (): Config =>
  createConfig({
    chains: [chains.ethereum, chains.base],
    connectors: [],
    transports: {
      [chains.ethereum.id]: transports.ethereum,
      [chains.base.id]: transports.base,
    },
    ssr: true,
  });

export const wagmiConfig: Config = env.SHELL_MODE
  ? shellConfig()
  : getDefaultConfig({
  appName: 'Persona',
  // Use a syntactically-valid placeholder (32 chars) so RainbowKit doesn't
  // throw at config time. Real WC connections still fail; that's fine since
  // we removed WC-based wallets from the list above when projectId is fake.
  projectId: HAS_REAL_WC_PROJECT
    ? env.WALLETCONNECT_PROJECT_ID
    : '00000000000000000000000000000000',
  wallets: walletsConfig,
  chains: [chains.ethereum, chains.base],
  transports: {
    [chains.ethereum.id]: transports.ethereum,
    [chains.base.id]: transports.base,
  },
  ssr: true,
});

export function WagmiProvider({ children }: PropsWithChildren) {
  return <BaseWagmiProvider config={wagmiConfig}>{children}</BaseWagmiProvider>;
}
